import Controller from '@ember/controller';
import { action } from '@ember/object';
import { trackedObject, trackedSet } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import type { HasMany } from '@ember-data/model';
import { cached, tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import type { Insightpagesfavourite } from 'frontend-utils/models';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type {
  SelectionsKey,
  SelectionsValue,
} from 'lumen/controllers/hotel/parity/insight';
import type {
  ParityInsightHistoricalData,
  ParityInsightSegmentationData,
  ParityInsightSegmentationItem,
} from 'lumen/models';
import type { BreakdownItem } from 'lumen/models/parity/insight/segmentation-item';
import type MarketParityInsightRoute from 'lumen/routes/market/parity/insight';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import type {
  MealTypeValues,
  MembershipType,
} from 'lumen/utils/constants/general-constants';
import { MEMBERSHIPTYPES } from 'lumen/utils/constants/general-constants';
import type {
  Breakdown,
  CountryCodeWithDefault,
  DateAggregation,
  FromDateType,
  ParityFilter,
  ParityFilterOrBenchmark,
  RateType,
  SelectInputOptionWithId,
  SortType,
} from 'lumen/utils/constants/pi-constant-values';
import {
  BENCHMARK,
  BREAKDOWNS_TO_HIDE_WHEN_EMPTY,
  DATE_AGGREGATION_MONTH,
  DATE_AGGREGATION_WEEK,
  LATEST,
  PARITY_FILTER,
  PARITY_FILTER_TRANSLATION_KEYS,
  PI_BREAKDOWNS,
  SORT_TYPES,
  SORTED_PI_BREAKDOWNS,
} from 'lumen/utils/constants/pi-constant-values';
import getTodayUtc from 'lumen/utils/dates/get-today-utc';
import type { ActiveFilter } from 'lumen/utils/filter-bar/filter-functions';
import {
  addFiltersToActiveFilters,
  getSegmentationValue,
} from 'lumen/utils/filter-bar/filter-functions';
import { logReportInput } from 'lumen/utils/log-report-input';
import {
  getFirstAllowedHistoricalExtractDate,
  getFirstFormattedHistoricalExtractDateInRange,
  getFormattedLastPossibleExtractDate,
  isExtractDateOutsideHistory,
  isHistoryNearEnd,
} from 'lumen/utils/parity-analytics/date-utils';
import { parseObject, stringifyObject } from 'lumen/utils/parse-utils';
import { formatUrl } from 'lumen/utils/rates-request';
import type { ModelFrom } from 'lumen/utils/routes';
import { timeout } from 'lumen/utils/waiters';

interface Filter {
  segmentationId: string;
  segmentationValueId: string;
}

interface ExportRequestParams {
  base_membership_type?: MembershipType;
  dateAggregation: DateAggregation;
  extractDate: string;
  historicalExtractDate: string;
  los: number;
  market_id: string;
  mealtype: MealTypeValues;
  pos: CountryCodeWithDefault;
  rate_type: RateType;
  segmentationFilters: string;
  sortKey: ParityFilter;
  sortType: SortType;
}

export type HotelViewFilters = {
  fromDateType: FromDateType;
  los: number;
  mealPlan: MealTypeValues;
  pos: CountryCodeWithDefault;
  rateType: RateType;
  showMemberRate: boolean;
};

const QUERY_PARAMS = [
  {
    segmentationFilters: { scope: 'controller' },
    historicalExtractDate: { scope: 'controller' },
    extractDate: { scope: 'controller' },
    benchmark: { scope: 'controller' },
    dateAggregation: { scope: 'controller' },
    sortType: { scope: 'controller' },
    sortKey: { scope: 'controller' },
    rateType: { scope: 'controller' },
    los: { scope: 'controller' },
    pos: { scope: 'controller' },
    mealPlan: { scope: 'controller' },
    showMemberRate: { scope: 'controller' },
    fromDateType: { scope: 'controller' },
  },
] as const;

export default class MarketParityInsightController extends Controller {
  declare model: ModelFrom<MarketParityInsightRoute>;

  // @ts-expect-error: Property 'type' is missing in type '{ readonly scope: "controller"; }' but required in type '{ type: ControllerQueryParamType; }'.
  queryParams: Controller['queryParams'] = QUERY_PARAMS;

  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeFilters: ActiveFilter[] = [];
  @tracked benchmark: boolean =
    DEFAULT_QP_VALUES.market.parity.insight.benchmark;
  @tracked dateAggregation: DateAggregation =
    DEFAULT_QP_VALUES.market.parity.insight.dateAggregation;
  @tracked extractDate: string =
    DEFAULT_QP_VALUES.market.parity.insight.extractDate;

  @tracked fromDateType: FromDateType =
    DEFAULT_QP_VALUES.market.parity.insight.fromDateType;

  @tracked historicalExtractDate: string =
    DEFAULT_QP_VALUES.market.parity.insight.historicalExtractDate;

  @tracked isFilterBarHovered = false;
  @tracked los: number = DEFAULT_QP_VALUES.market.parity.insight.los;
  @tracked mealPlan: MealTypeValues =
    DEFAULT_QP_VALUES.market.parity.insight.mealPlan;
  @tracked pos: CountryCodeWithDefault =
    DEFAULT_QP_VALUES.market.parity.insight.pos;

  @tracked rateType: RateType =
    DEFAULT_QP_VALUES.market.parity.insight.rateType;

  // URL parameter containing the JSON.stringified filters
  @tracked segmentationFilters: string =
    DEFAULT_QP_VALUES.market.parity.insight.segmentationFilters;
  @tracked showMemberRate: boolean = false;

  @tracked sortKey: ParityFilter =
    DEFAULT_QP_VALUES.market.parity.insight.sortKey;
  @tracked sortType: SortType =
    DEFAULT_QP_VALUES.market.parity.insight.sortType;

  parityFilters = trackedSet<ParityFilter>([
    PARITY_FILTER.WIN,
    PARITY_FILTER.MEET,
  ]);
  searchValuesPerSegmentationId = trackedObject<
    Partial<
      Record<
        Breakdown,
        {
          isSearchActive: boolean;
          searchValue: string;
        }
      >
    >
  >();
  unhandledSegmentationFiltersClicked: Filter[] = [];

  segmentationFilterClickedTask = task(
    { restartable: true },
    async (
      segmentationId: string,
      segmentationValueId: string,
    ): Promise<void> => {
      if (segmentationId === PI_BREAKDOWNS.SUBSCRIPTION_ID) {
        this.usageAnalytics.track('Hotel Filter Applied', {
          eventTrigger: 'hotel-widget',
          hotelId: parseInt(segmentationValueId, 10),
        });
      } else {
        this.usageAnalytics.track('Breakdown Filter Applied', {
          eventTrigger: 'breakdown-widget',
          filterType: segmentationId,
          filterValue: segmentationValueId,
        });
      }

      this.unhandledSegmentationFiltersClicked.push({
        segmentationId,
        segmentationValueId,
      });
      await timeout();
      this.debouncedSegmentationfilterClicked();
    },
  );

  get allBlocksData(): Record<
    Breakdown,
    { segmentationName: Breakdown; segmentationObjects?: BreakdownItem[] }
  > {
    const allBlocksData = {} as Record<
      Breakdown,
      { segmentationName: Breakdown; segmentationObjects?: BreakdownItem[] }
    >;

    this.segmentationBlocks.forEach((segmentation) => {
      allBlocksData[segmentation.segmentationName] = segmentation;
    });

    if (this.hotelsBlockData) {
      allBlocksData[PI_BREAKDOWNS.SUBSCRIPTION_ID] = this.hotelsBlockData;
    }

    return allBlocksData;
  }

  get allData(): ParityInsightSegmentationItem | undefined {
    return this.segmentationData?.all[0];
  }

  get dateRange(): { end: Dayjs; start: Dayjs } {
    const startMoment = dayjs(this.extractDateForDatePicker, 'YYYY-MM-DD');

    return {
      start: startMoment.startOf(this.isoDateAggregation),
      end: startMoment.endOf(this.isoDateAggregation).startOf('day'),
    };
  }

  // extractDate for latest is not last Monday by default
  get extractDateForDatePicker(): string {
    if (this.isLatestSelected) {
      const lastMonday = dayjs().utc().startOf('isoWeek').format('YYYY-MM-DD');

      return lastMonday;
    }

    return this.extractDate;
  }

  get extractDateKey(): string {
    if (this.dateAggregation === DATE_AGGREGATION_MONTH) {
      // Month keys are formatted YYYY-MM
      return this.extractDate.substring(0, 7);
    }

    // DATE_AGGREGATION_WEEK logic
    if (
      dayjs(this.extractDate).isAfter(getFormattedLastPossibleExtractDate())
    ) {
      return LATEST;
    }

    // Week keys are formatted YYYY-MM-DD
    return this.extractDate;
  }

  get favourites(): Insightpagesfavourite[] {
    return Array.from(
      this.hotelAndMarketService.hotelOrMarket.favouriteFilters,
    );
  }

  get formattedExtractRange(): string {
    if (this.dateAggregation === DATE_AGGREGATION_MONTH) {
      return this.intl.t('parity_analytics.headers.latest', {
        period: this.intl.t('general.month'),
        formattedExtractDate: dayjs(this.extractDate).format(
          this.dates.partialDateFormat.YEAR_AND_MONTH_LONG,
        ),
      });
    }

    if (this.isLatestSelected) {
      return this.intl.t('parity_analytics.headers.shop_results.weekly', {
        formattedExtractDate: this.intl
          .t('benchmark.current_score')
          .toLowerCase(),
      });
    }

    const weekFormat = 'LL';
    return this.intl.t('parity_analytics.headers.range', {
      formattedExtractDate: dayjs(this.extractDate).format(weekFormat),
      formattedExtractDateEndOfWeek: dayjs(this.extractDate)
        .add(6, 'day')
        .format(weekFormat),
    });
  }

  get hasHistoricalRequestError(): boolean {
    return this.model.historicalData.isError;
  }

  get hasSegmentationRequestError(): boolean {
    return this.model.segmentationData.isError;
  }

  get historicalChartData(): ParityInsightHistoricalData | null {
    return this.model.historicalData.value;
  }

  get historicalExtractDateMoment(): Dayjs {
    return dayjs(this.historicalExtractDate, 'YYYY-MM-DD');
  }

  get hotelsBlockData(): {
    segmentationName: Breakdown;
    segmentationObjects?: BreakdownItem[];
  } {
    if (this.isLoadingSegmentationData) {
      return {
        segmentationName: PI_BREAKDOWNS.SUBSCRIPTION_ID,
        segmentationObjects: [],
      };
    }

    return {
      segmentationName: PI_BREAKDOWNS.SUBSCRIPTION_ID,
      segmentationObjects:
        this.segmentationData?.[PI_BREAKDOWNS.SUBSCRIPTION_ID],
    };
  }

  get hotelViewFilters(): HotelViewFilters {
    return {
      fromDateType: this.fromDateType,
      los: this.los,
      mealPlan: this.mealPlan,
      pos: this.pos,
      rateType: this.rateType,
      showMemberRate: this.showMemberRate,
    };
  }

  get isBenchmarkDisabled(): boolean {
    return this.parityFilters.size === 0 || this.parityFilters.size === 3;
  }

  get isBenchmarkVisible(): boolean {
    return !this.isBenchmarkDisabled && this.benchmark;
  }

  get isLatestSelected(): boolean {
    return dayjs(this.extractDate).isAfter(
      getFormattedLastPossibleExtractDate(),
    );
  }

  get isLoading(): boolean {
    return this.isLoadingSegmentationData || this.isLoadingHistoricalData;
  }

  get isLoadingGaugeData(): boolean {
    if (this.selectedPeriodFromHistoricalData) {
      return false;
    }
    return this.isLoadingSegmentationData;
  }

  get isLoadingHistoricalData(): boolean {
    return this.model.historicalData.isRunning;
  }

  get isLoadingSegmentationData(): boolean {
    return this.model.segmentationData.isRunning;
  }

  get isoDateAggregation(): 'month' | 'isoWeek' {
    return this.dateAggregation === DATE_AGGREGATION_MONTH
      ? DATE_AGGREGATION_MONTH
      : 'isoWeek';
  }

  get issueTypeData(): HasMany<ParityInsightSegmentationItem> | undefined {
    return this.segmentationData?.issue_type;
  }

  get maxDate(): string {
    if (this.dateAggregation === DATE_AGGREGATION_MONTH) {
      return getFormattedLastPossibleExtractDate();
    }

    return getTodayUtc();
  }

  get maxDateMoment(): Dayjs {
    return this.hotelAndMarketService
      .toLocalTime(this.maxDate)
      .endOf(this.isoDateAggregation);
  }

  get minDateMoment(): Dayjs {
    return this.hotelAndMarketService.toLocalTime(
      this.hotelAndMarketService.hotelOrMarket.parityFirstExtractDate,
    );
  }

  get parsedSegmentationFilters(): Record<string, string[]> {
    return parseObject(this.segmentationFilters) as Record<string, string[]>;
  }

  get segmentationBlocks(): {
    segmentationName: Breakdown;
    segmentationObjects: BreakdownItem[];
  }[] {
    if (this.isLoadingSegmentationData || !this.segmentationData) {
      return [];
    }

    const sortedAndFilteredBreakdownIds = SORTED_PI_BREAKDOWNS.filter((key) => {
      // PI HLA doesn't have all the breakdowns
      if (this.segmentationData?.[key] === undefined) {
        return false;
      }

      const hasBreakdownItems = this.segmentationData[key].length > 0;
      return !(
        BREAKDOWNS_TO_HIDE_WHEN_EMPTY.includes(key) && !hasBreakdownItems
      );
    });

    const breakdownBlocks: {
      segmentationName: Breakdown;
      segmentationObjects: BreakdownItem[];
    }[] = [];
    const segmentationObjectsGroupedById: Partial<
      Record<Breakdown, Record<string, BreakdownItem>>
    > = {};

    sortedAndFilteredBreakdownIds.forEach((segmentationKey) => {
      const segmentationBlockStructure = {
        segmentationName: segmentationKey,
        segmentationObjects: this.segmentationData?.[
          segmentationKey
        ] as BreakdownItem[],
      };

      if (!segmentationObjectsGroupedById[segmentationKey]) {
        segmentationObjectsGroupedById[segmentationKey] = {};
      }

      if (this.segmentationData?.[segmentationKey].length) {
        this.segmentationData[segmentationKey].forEach((segmentationObject) => {
          const record = segmentationObjectsGroupedById[
            segmentationKey
          ] as Record<string, BreakdownItem>;
          record[segmentationObject.segmentationValueId] = segmentationObject;
        });
      }

      breakdownBlocks.push(segmentationBlockStructure);
    });

    return breakdownBlocks;
  }

  get segmentationData(): ParityInsightSegmentationData | null {
    return this.model.segmentationData.value;
  }

  get selectedPeriodData(): ParityInsightSegmentationItem | undefined {
    // Quick-fetch the selected period data from the historical data (if it's within range).
    // Otherwise, fetch it from the (larger) breakdown request.
    return (
      this.selectedPeriodFromHistoricalData ??
      this.selectedPeriodFromBreakdownData
    );
  }

  get selectedPeriodFromBreakdownData():
    | ParityInsightSegmentationItem
    | undefined {
    return this.segmentationData?.all?.[0];
  }

  get selectedPeriodFromHistoricalData():
    | ParityInsightSegmentationItem
    | undefined {
    return this.historicalChartData?.all?.[this.extractDateKey]?.[0];
  }

  get selectedSortKey(): SelectInputOptionWithId<ParityFilter> | undefined {
    return this.sortKeys.filter((sortKey) => sortKey.id === this.sortKey)[0];
  }

  get selectedSortType(): SelectInputOptionWithId<SortType> | undefined {
    return this.sortTypes.filter(
      (sortType) => sortType.id === this.sortType,
    )[0];
  }

  get selectedTimespan(): SelectInputOptionWithId<DateAggregation> | undefined {
    return this.timespans.filter(
      (timespan) => timespan.id === this.dateAggregation,
    )[0];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get sortKeys(): SelectInputOptionWithId<ParityFilter>[] {
    return [
      {
        id: PARITY_FILTER.LOSS,
        label: this.intl.t(PARITY_FILTER_TRANSLATION_KEYS[PARITY_FILTER.LOSS]),
      },
      {
        id: PARITY_FILTER.MEET,
        label: this.intl.t(PARITY_FILTER_TRANSLATION_KEYS[PARITY_FILTER.MEET]),
      },
      {
        id: PARITY_FILTER.WIN,
        label: this.intl.t(PARITY_FILTER_TRANSLATION_KEYS[PARITY_FILTER.WIN]),
      },
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get sortTypes(): SelectInputOptionWithId<SortType>[] {
    return [
      {
        id: SORT_TYPES.ABSOLUTE,
        label: this.intl.t('parity_analytics.number_of_shops'),
      },
      {
        id: SORT_TYPES.RELATIVE,
        label: this.intl.t('parity_analytics.sort.relative'),
      },
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get timespans(): SelectInputOptionWithId<DateAggregation>[] {
    return [
      { id: DATE_AGGREGATION_WEEK, label: this.intl.t('ranking.weekly') },
      { id: DATE_AGGREGATION_MONTH, label: this.intl.t('ranking.monthly') },
    ];
  }

  get varianceData(): HasMany<ParityInsightSegmentationItem> | undefined {
    return this.segmentationData?.variance_bucket;
  }

  constructor(...args: ConstructorParameters<typeof Controller>) {
    super(...args);

    // Make a default empty search state for all blocks where you can filter
    Object.values(PI_BREAKDOWNS).forEach((segmentationId) => {
      this.searchValuesPerSegmentationId[segmentationId] = trackedObject({
        isSearchActive: false,
        searchValue: '',
      });
    });
  }

  /**
   * Needed for the filter bar addon utils
   * Will format the segmentation id for display in the filter bar
   * @param  { string } segmentationId : Id of the block
   * @return { string }                : formatted id, in this case a translation key
   */
  segmentationIdFormatter(this: void, segmentationId: Breakdown): string {
    return `parity_analytics.${segmentationId}`;
  }

  @action dateAggregationChanged(
    dateAggregationOption: SelectInputOptionWithId<DateAggregation>,
  ): void {
    this.usageAnalytics.track('Date View Changed', {
      eventTrigger: 'filter-bar',
      periodType: dateAggregationOption.id,
    });
    this.dateAggregation = dateAggregationOption.id;
  }

  @action debouncedSegmentationfilterClicked(): void {
    // This code is called when the selected filters need to change. The segmentation object of the filter to be updated is passed as a parameter
    // Default behaviour: remove the filter if it's already present in the active filters / add the filter if it's not present in the active filters

    // Note: use segmentation_value_id as URL parameter.
    // In case of a very long segmentation_value, this will be a truncated version

    // All unhandled segmentationFilters will be present here
    // We need to take a deep copy so we can clear the array immediately after
    const unhandledSegmentationFiltersClicked = [
      ...this.unhandledSegmentationFiltersClicked,
    ];
    this.unhandledSegmentationFiltersClicked = [];

    unhandledSegmentationFiltersClicked.forEach((segmentationValueObj) => {
      // Needs to be parsed since we'll edit the object
      const currentFilters = parseObject(this.segmentationFilters) as Record<
        string,
        string[]
      >;

      const { segmentationId, segmentationValueId } = segmentationValueObj;
      if (currentFilters[segmentationId]?.includes(segmentationValueId)) {
        // Remove the clicked filter if it was an active filter
        const index =
          currentFilters[segmentationId].indexOf(segmentationValueId);
        currentFilters[segmentationId].splice(index, 1);
        // Remove the reference to the breakdown as well if there are no more filters from this breakdown
        if (currentFilters[segmentationId].length === 0) {
          delete currentFilters[segmentationId];
        }
      } else if (currentFilters[segmentationId]) {
        // Add a filter of a breakdown we were already filtering on
        currentFilters[segmentationId].push(segmentationValueId);
      } else {
        // Add a filter of a breakdown we weren't filtering on yet
        currentFilters[segmentationId] = [segmentationValueId];
      }

      this.segmentationFilters = stringifyObject(currentFilters);
    });
  }

  // Any change made to the filters, or if the model becomes available, we should update the filter bar data
  @action displayedSegmentationFiltersObserver(): void {
    const filters = parseObject(this.segmentationFilters) as Record<
      string,
      string[]
    >;
    this.activeFilters = addFiltersToActiveFilters(
      this.activeFilters,
      filters,
      this.segmentationIdFormatter,
      // eslint-disable-next-line @typescript-eslint/unbound-method -- bound via @action
      this.segmentationValueObjectFormatter,
    );
  }

  @action handleExport(): void {
    const exportParams: ExportRequestParams = {
      market_id: this.hotelAndMarketService.market.id!,
      extractDate: this.extractDate,
      historicalExtractDate: this.historicalExtractDate,
      dateAggregation: this.dateAggregation,
      sortType: this.sortType,
      sortKey: this.sortKey,
      segmentationFilters: this.segmentationFilters,
      rate_type: this.rateType,
      los: this.los,
      pos: this.pos,
      mealtype: this.mealPlan,
    };

    if (this.showMemberRate) {
      exportParams['base_membership_type'] = MEMBERSHIPTYPES.MEMBER_ONLY;
    }

    const url = formatUrl(
      '/apigateway/v1/app/download/parityanalytics',
      exportParams,

      this.ajax.host,
    );

    this.usageAnalytics.track('Data Exported', {
      eventTrigger: 'filter-bar',
    });

    logReportInput(url, this.access.isStaffUser);

    openUrl(url);
  }

  @action periodChanged(selectedPeriod: Dayjs): void {
    this.router.transitionTo({
      queryParams: { extractDate: selectedPeriod.format('YYYY-MM-DD') },
    });
  }

  @action periodChangedFromDatePicker(
    selectedRange: typeof this.dateRange,
  ): void {
    // When a user starts filtering and then changes the date while the request is still running, this will be undefined
    if (selectedRange) {
      // If we don't go to the start of the period, the week/month-picker dropdown trigger would show the day you clicked on if it was within the previously selected range (not allowed)
      const selectedPeriodAtStart = selectedRange.start;

      this.usageAnalytics.track('Date Changed', {
        eventTrigger: 'filter-bar',
        startDaysOffsetFromToday: selectedPeriodAtStart.diff(
          this.hotelAndMarketService.getLocalTime(),
          'days',
        ),
      });

      // We need to check if the period you just clicked on in the picker isn't outside of the history chart view
      // We need to update the evolution chart if it is outside of its scope
      if (
        isExtractDateOutsideHistory(
          selectedPeriodAtStart,
          this.historicalExtractDate,
          this.dateAggregation,
        )
      ) {
        if (
          isHistoryNearEnd(
            this.hotelAndMarketService.hotelOrMarket.parityFirstExtractDate,
            selectedPeriodAtStart.format('YYYY-MM-DD'),
            this.dateAggregation,
          )
        ) {
          this.historicalExtractDate = getFirstAllowedHistoricalExtractDate(
            this.hotelAndMarketService.hotelOrMarket.parityFirstExtractDate,
            this.dateAggregation,
          ).format('YYYY-MM-DD');
        } else {
          this.historicalExtractDate =
            selectedPeriodAtStart.format('YYYY-MM-DD');
        }

        this.extractDate = selectedPeriodAtStart.format('YYYY-MM-DD');
      } else {
        this.send('periodChanged', selectedPeriodAtStart);
      }
    }
  }

  @action redirectToTrends(segmentation: Breakdown): void {
    // Trends doesn't have latest so show getFormattedLastPossibleExtractDate instead
    const extractDateBase = dayjs
      .min(
        dayjs(this.extractDate),
        dayjs(getFormattedLastPossibleExtractDate()),
      )
      .format('YYYY-MM-DD');

    this.usageAnalytics.track('Breakdown Detailed View Opened', {
      eventTrigger: 'detailed-view-button',
      breakdownId: segmentation,
    });

    this.router.transitionTo('market.parity.insight.trends', {
      queryParams: {
        segmentation,
        trendsExtractDate: extractDateBase,
        selection: this.sortKey,
      },
    });
  }

  /**
   * Needed for the filter bar addon utils
   * Will format the segmentation value id for display in the filter bar
   * @param  { string } segmentationId        : id of the block
   * @param  { string } segmentationValueId   : id of the value
   */
  @action
  segmentationValueObjectFormatter(
    segmentationId: Breakdown,
    segmentationValueId: string,
  ): string {
    const objSegmentationValue = getSegmentationValue(
      this.allBlocksData,
      segmentationId,
      segmentationValueId,
      'segmentationValueId',
    );

    // If it's not found this means the model is not loaded yet
    if (objSegmentationValue) {
      return objSegmentationValue.formattedSegmentationValue;
    }

    // Display N/A if the filter value can't be found in the response
    return this.intl.t('general.not_applicable');
  }

  @action selectionsChanged(
    queryParam: SelectionsKey,
    value: SelectionsValue,
  ): void {
    this.router.transitionTo({ queryParams: { [queryParam]: value } });

    this.usageAnalytics.track('Filter Applied', {
      eventTrigger: 'filter-bar',
      filterType: queryParam,
      filterValue: value,
    });
  }

  // Used in filter bar for setting favourites
  @action setFilterValues(updatedFilterValues: {
    segmentationFilters: string;
  }): void {
    this.segmentationFilters = updatedFilterValues.segmentationFilters;
  }

  @action setIsFilterBarHovered(isFilterBarHovered: boolean): void {
    this.isFilterBarHovered = isFilterBarHovered;
  }

  @action setIsSearchActive(
    segmentationId: Breakdown,
    isSearchActive: boolean,
  ): void {
    if (!this.searchValuesPerSegmentationId[segmentationId]) {
      return;
    }

    this.searchValuesPerSegmentationId[segmentationId].isSearchActive =
      isSearchActive;
  }

  @action setSearchValue(segmentationId: Breakdown, searchValue: string): void {
    if (!this.searchValuesPerSegmentationId[segmentationId]) {
      return;
    }

    this.searchValuesPerSegmentationId[segmentationId].searchValue =
      searchValue;
  }

  @action sortKeyChanged(sortKey: SelectInputOptionWithId<ParityFilter>): void {
    this.sortKey = sortKey.id;
  }

  @action sortTypeChanged(sortType: SelectInputOptionWithId<SortType>): void {
    this.sortType = sortType.id;
  }

  @action toggleMemberRate(): void {
    this.usageAnalytics.track('Member Rate Toggled', {
      eventTrigger: 'filter-bar',
      memberRateStatus: this.showMemberRate ? 'off' : 'on',
    });

    this.router.transitionTo({
      queryParams: { showMemberRate: !this.showMemberRate },
    });
  }

  @action updateHistoricalPeriod(extractDate: Dayjs): void {
    const historicalExtractDate = extractDate.isAfter(
      this.maxDate,
      this.dateAggregation,
    )
      ? this.maxDate
      : extractDate.format('YYYY-MM-DD');

    this.usageAnalytics.track('Shop Date Evolution Explored', {
      eventTrigger: 'picker-period',
      periodType: this.dateAggregation,
      periodStart: getFirstFormattedHistoricalExtractDateInRange(
        dayjs(historicalExtractDate),
        this.dateAggregation,
      ),
      periodEnd: historicalExtractDate,
    });

    this.router.transitionTo({
      queryParams: { historicalExtractDate },
    });
  }

  @action updateParityFilters(filter: ParityFilterOrBenchmark): void {
    const isBenchmarkClicked = filter === BENCHMARK;
    if (!isBenchmarkClicked) {
      if (this.parityFilters.has(filter)) {
        this.parityFilters.delete(filter);
      } else {
        this.parityFilters.add(filter);
      }
    } else if (!this.isBenchmarkDisabled) {
      this.benchmark = !this.benchmark;
    }
  }
}
